// The Movie Planner page's data shapes and the pure state transitions over
// them. The server computes the display fields (see
// `server/services/planner_view.py`); what lives here is only what has to
// happen between a click and the refetch that confirms it.

export type SeatVisit = { seats: string; movie: string; date: string };

export type AuditoriumHistory = {
  first_time: boolean;
  summary: string;
  entries: SeatVisit[];
};

/** "Stay for credits" for a plan's film. `verdict: null` means it has not
 *  been looked up yet. `recheck_at` is set for an `unknown` that the server
 *  will look at again 30 minutes before the screening (ISO, with offset). */
export type StayForCredits = {
  verdict: "yes" | "no" | "unknown" | null;
  mid_credits: boolean | null;
  post_credits: boolean | null;
  recheck_at: string | null;
};

export type PlannedScreening = {
  date: string;
  day_label: string;
  time: string;
  time_range: string;
  movie_title: string;
  /** True when this film is already in the week's Movies Seen rows, so it is
   *  not spending a slot of its own. Removing it frees nothing. */
  already_seen: boolean;
  theater: string;
  theater_short: string;
  format: string;
  auditorium: string;
  auditorium_history: AuditoriumHistory | null;
  price: number | null;
  seats: string;
  /** Present only for a link that opens an actual seat map. Null covers the
   *  theater/date fallback URLs, which is every row while AMC's queue is up. */
  ticket_url: string | null;
  runtime_minutes: number | null;
  has_ended: boolean;
  stay_for_credits: StayForCredits;
};

/** What the page should ask `/api/planner/credits` about: films never looked
 *  up, and unknowns whose recheck time has arrived. Returned as a stable key
 *  so the page asks once per change rather than on every re-render. */
export function creditsDueKey(body: PlannerBody | null, now: number): string {
  if (!body) return "";
  const due = body.weeks
    .flatMap((w) => w.plans)
    .filter((p) => !p.has_ended)
    .filter((p) => {
      const c = p.stay_for_credits;
      if (!c) return false;
      if (c.verdict === null) return true;
      return c.recheck_at !== null && Date.parse(c.recheck_at) <= now;
    })
    .map((p) => p.movie_title);
  return [...new Set(due)].sort().join("\n");
}

/** The next future recheck, in ms since the epoch, or null. */
export function nextCreditsRecheck(body: PlannerBody | null, now: number): number | null {
  if (!body) return null;
  const times = body.weeks
    .flatMap((w) => w.plans)
    .map((p) => p.stay_for_credits?.recheck_at)
    .filter((t): t is string => !!t)
    .map((t) => Date.parse(t))
    .filter((t) => !Number.isNaN(t) && t > now);
  return times.length ? Math.min(...times) : null;
}

export function creditsLabel(c: StayForCredits): { text: string; title: string } {
  if (c.verdict === "yes") {
    const scenes = [
      c.mid_credits ? "a mid-credits scene" : null,
      c.post_credits ? "a post-credits scene" : null,
    ].filter(Boolean);
    return {
      text: "Stay for credits: Yes",
      title: scenes.length ? `Has ${scenes.join(" and ")}` : "Has a scene in or after the credits",
    };
  }
  if (c.verdict === "no") {
    return { text: "Stay for credits: No", title: "Nothing during or after the credits" };
  }
  if (c.verdict === "unknown") {
    return {
      text: "Stay for credits: Unknown",
      title: c.recheck_at
        ? "No answer found yet; checking again 30 minutes before the showtime"
        : "No answer found",
    };
  }
  return { text: "Stay for credits: checking…", title: "Looking this up" };
}

export type PlannerWeek = {
  week_key: string;
  label: string;
  seen: number;
  planned: number;
  already_seen: number;
  used: number;
  limit: number;
  remaining: number;
  full: boolean;
  plans: PlannedScreening[];
};

export type PlannerBody = {
  available: boolean;
  weeks: PlannerWeek[];
  total: number;
  limit: number;
};

export type PlannerSuggestion = {
  /** "Sat Aug 15" and "2:25pm-4:10pm", formatted server-side by the SAME
   *  helpers the plan table uses so the two read identically. The first time
   *  in `time_range` is always the START. */
  day_label: string;
  time_range: string;
  date: string;
  time: string;
  movie_title: string;
  theater: string;
  format: string;
  ticket_url: string;
  runtime_minutes: number | null;
  week_key: string;
  reason: string;
  double_feature: boolean;
  /** Why the FILM was suggested, from throw-rag: `watchlist` (an explicit
   *  want), `seen` (watched and loved), `watched`, or `similar` (an unseen
   *  match on genre and theme). Empty when the hub was unreachable and the
   *  plan fell back to unranked candidates. Deliberately separate from
   *  `reason`, which is the model's argument for this SLOT: one is a fact
   *  about the watch history, the other is a scheduling opinion. */
  taste_basis?: string;
  taste_reason?: string;
  /** This was the film's only usable screening in the whole horizon. */
  only_option?: boolean;
  /** Distinct dates this film plays in the horizon, off the raw listings.
   *  Null when the film has not opened yet, because the count then measures
   *  how far ahead AMC has published rather than the length of the run. */
  run_dates?: number | null;
  /** Fewer than a week of dates -- a repertory run or a one-off event. The
   *  only run-shape claim this data supports: "leaving soon" was removed
   *  2026-08-13 because a film's last listed date is set by AMC's on-sale
   *  window, not by when its run ends. */
  limited_engagement?: boolean;
  /** When the film opened (`YYYY-MM-DD`), from the `First Seen` registry. */
  opening_date?: string;
  /** That date already phrased -- "opens Aug 20", "opens today", "opened Aug
   *  6" -- tensed against today rather than against the suggested screening,
   *  because the question is how far into its run the film is *now*, while you
   *  are deciding. Empty means UNKNOWN, never "opened long ago": the registry
   *  column shipped 2026-08-12 and can never name an opening date for a film
   *  already running then. Formatted server-side for the same reason a
   *  rating's `display` is -- one copy of the rule, shared with the Finder's
   *  badge. */
  opening_label?: string;
  /** "re-release · 1991" -- EXCLUSIVE with `opening_label`, which the server
   *  blanks whenever this is set. A film over a year old cannot be in its
   *  first run, and the row already names the date and time you would go, so
   *  "not a new film" is the fact worth the space. Empty for anything recent,
   *  where it would only repeat the opening. */
  rerelease_label?: string;
};

export type PlannerSuggestionsBody = {
  available: boolean;
  reason?: string;
  generated_at: string;
  horizon_weeks: number;
  unavailable_dates: string[];
  /** False when the throw-rag hub could not be reached, so every eligible
   *  screening went to the model rather than only the taste-matched ones.
   *  Absent on the early returns that never got as far as ranking. */
  taste_ranked?: boolean;
  weeks: Array<{
    week_key: string;
    /** "Week of Aug 14 - 20", the same label the plan table below shows. */
    label: string;
    suggestions: PlannerSuggestion[];
    /** Slots this week already spent before any suggestion: planned + seen. */
    already_used: number;
    /** The two halves of `already_used`. Booked-not-yet-watched and watched
     *  are different states -- one you could still change -- and the summary
     *  said "planned or seen" because the server only sent the total. */
    already_planned: number;
    already_seen: number;
    /** How many of the suggestions below are new. */
    suggested: number;
    /** The week's WHOLE allowance used: suggestions + already_used. */
    filled: number;
    limit: number;
    remaining: number;
  }>;
};

/** The four fields a plan is identified by, everywhere. */
export function planIdentity(plan: PlannedScreening) {
  return {
    date: plan.date,
    time: plan.time,
    movie_title: plan.movie_title,
    theater: plan.theater,
  };
}

export function samePlan(a: PlannedScreening, b: PlannedScreening): boolean {
  return (
    a.date === b.date &&
    a.time === b.time &&
    a.movie_title === b.movie_title &&
    a.theater === b.theater
  );
}

/** A week's slot arithmetic, recomputed from its rows.
 *
 *  Kept as one function because `used` is not `seen + planned`: a plan whose
 *  film was already logged that week is credited to its Movies Seen row, not
 *  to a second slot. Recomputing the parts separately at each call site is how
 *  the two halves drift.
 *
 *  A blank `week_key` spends nothing, so its `used` carries over untouched.
 *  An unparseable week cannot be scored against an allowance: `is_full` guards
 *  on `bool(week_key)` outright, and `used` returns zero because
 *  `PlannerState.__init__` skips those plans and never creates the key. This
 *  bucket only became reachable here once the server stopped 500ing on a
 *  corrupt Week Key cell and started listing it so it can be deleted -- before
 *  that the client never saw one, and recomputing would invent a usage the
 *  server always reports as zero.
 */
function recount(week: PlannerWeek, plans: PlannedScreening[]): PlannerWeek {
  const alreadySeen = plans.filter((p) => p.already_seen).length;
  const scored = Boolean(week.week_key);
  const used = scored ? week.seen + plans.length - alreadySeen : week.used;
  return {
    ...week,
    plans,
    planned: plans.length,
    already_seen: alreadySeen,
    used,
    remaining: Math.max(0, week.limit - used),
    // `scored &&`, not just the arithmetic: `is_full` guards on
    // `bool(week_key)` OUTRIGHT, so an unscored week is never full whatever
    // its `used` says. Deriving `full` from `used` alone happens to agree
    // today only because the server never sends a non-zero `used` for a blank
    // key -- it would stop agreeing the moment it did.
    full: scored && used >= week.limit,
  };
}

/** The body keeping only the plans `keep` accepts, weeks recounted.
 *
 *  A week left with no plans is dropped entirely, matching what the server
 *  would return: weeks come from the planner, so a week with nothing planned
 *  has no header to draw.
 */
function keepPlans(
  body: PlannerBody,
  keep: (plan: PlannedScreening) => boolean,
): PlannerBody {
  const weeks = body.weeks
    .map((week) => {
      const plans = week.plans.filter(keep);
      return plans.length === week.plans.length ? week : recount(week, plans);
    })
    .filter((week) => week.plans.length > 0);
  return { ...body, weeks, total: weeks.reduce((n, w) => n + w.plans.length, 0) };
}

/** The body with one plan dropped, applied optimistically before the refetch. */
export function removePlan(body: PlannerBody, target: PlannedScreening): PlannerBody {
  return keepPlans(body, (plan) => !samePlan(plan, target));
}

/** The body with one plan's seats replaced. */
export function setSeats(
  body: PlannerBody,
  target: PlannedScreening,
  seats: string,
): PlannerBody {
  return {
    ...body,
    weeks: body.weeks.map((week) => ({
      ...week,
      plans: week.plans.map((plan) =>
        samePlan(plan, target) ? { ...plan, seats } : plan,
      ),
    })),
  };
}

/** The body with every past-dated plan dropped, for the Clear past control.
 *
 *  Optimistic only. The SERVER decides the real boundary, in Pacific, over the
 *  whole sheet -- so this is a guess good enough to avoid a flash of stale
 *  rows, and the caller refetches afterwards to reconcile. That is why it is
 *  the one mutation whose handler refreshes on success as well as failure.
 *
 *  `today` is passed in rather than read from a clock so the boundary is
 *  testable, and compared as a string because both sides are YYYY-MM-DD, which
 *  sorts lexicographically. Parsing them into Dates would reintroduce the UTC
 *  rollover this repo keeps hitting. A malformed date sorts wherever it sorts
 *  and simply survives the optimistic pass; the refetch settles it.
 */
export function removePastPlans(body: PlannerBody, today: string): PlannerBody {
  return keepPlans(body, (plan) => plan.date >= today);
}


/** The grey summary beside a suggestion week's heading.
 *
 *  Pure and exported so the wording is testable without rendering the page --
 *  it has four inputs and a plural on each, which is more branching than a
 *  JSX expression should carry.
 *
 *  Says only what is non-zero. "3 suggested, 1 already planned" on a future
 *  week is the common case and must not read "planned or seen" when nothing
 *  has been seen yet; a week you have finished says "4 already seen" and no
 *  more. When both halves are non-zero they are named separately rather than
 *  merged, because that is the whole point of splitting them.
 */
export function weekSummary(week: {
  suggested: number;
  already_planned: number;
  already_seen: number;
}): string {
  const parts: string[] = [];
  if (week.suggested > 0) parts.push(`${week.suggested} suggested`);
  const spent: string[] = [];
  if (week.already_planned > 0) spent.push(`${week.already_planned} planned`);
  if (week.already_seen > 0) spent.push(`${week.already_seen} seen`);
  // "already" attaches to the first of the two, so a week reads "1 planned,
  // 2 seen" rather than repeating the word.
  if (spent.length > 0) parts.push(`already ${spent.join(", ")}`);
  if (parts.length === 0) return "";
  if (parts.length === 1 && week.suggested > 0) return "all suggested";
  return parts.join(", ");
}
